import {
  Button,
  ButtonList,
  FlexItem,
  Icon,
  ISortPayload,
  ITableColumn,
  Position,
  SortDirection,
  TextAlign,
  Theme,
  Toast,
} from '@busybusy/webapp-react-ui';
import { TableCellHeight } from '@busybusy/webapp-react-ui/dist/components/Table/types/types';
import {
  Equipment,
  EquipmentSearch,
  EquipmentSearchFields,
  OrganizationCustomFieldsTypes,
  QueryManageEquipmentQueryVariables,
  SearchType,
} from '__generated__/graphql';
import { ListIcon } from 'assets/icons';
import classNames from 'classnames';
import { EmptyState, FeatureUnavailable, MoreButton, Panel, PanelContent } from 'components';
import EquipmentCreateFormDialog from 'components/domain/equipment/CreateEquipmentDialog/CreateEquipmentDialog';
import CSVImportEquipmentContainer from 'components/domain/equipment/CSVImportEquipment/CSVImportEquipmentContainer';
import { convertManageEquipmentToCsvEquipment } from 'components/domain/equipment/CSVImportEquipment/utils/utils';
import ManageEquipmentCategoriesDialog from 'components/domain/equipment/ManageEquipmentCategoriesDialog/ManageEquipmentCategoriesDialog';
import ManageEquipmentMakesDialog from 'components/domain/equipment/ManageEquipmentMakesDialog/ManageEquipmentMakesDialog';
import CustomFieldsDialog from 'components/foundation/dialogs/CustomFieldsDialog/CustomFieldsDialog';
import RowSettingsDialog from 'components/foundation/dialogs/RowSettingsDialog/RowSettingsDialog';
import TemplatedTable from 'components/foundation/table/TemplatedTable/TemplatedTable';
import LegacyMainHeader from 'components/layout/LegacyMainHeader/LegacyMainHeader';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useActiveMember, useOpenable, useOrganization, useQueryParams } from 'hooks';
import useManagementAnalytics from 'hooks/analytics/useManagementAnalytics';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import useEquipment from 'hooks/models/equipment/useEquipment';
import useEquipmentManagementSettingsUpdate from 'hooks/models/member-settings/useEquipmentManagementSettingsUpdate';
import useManagementSettingsUpdate from 'hooks/models/member-settings/useManagementSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import _, { compact, isNil, partition } from 'lodash';
import Papa from 'papaparse';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Navigate } from 'react-router-dom';
import { ClassName } from 'types/ClassName';
import IEquipment from 'types/Equipment';
import { IVisibleTableColumn } from 'types/TableColumn';
import ITableRowHeightSettings from 'types/TableRowHeightSettings';
import { Nullable } from 'types/util/Nullable';
import { getGraphQlEqualComparison } from 'utils/apolloUtils';
import { mergeListsWithOverride } from 'utils/collectionUtils';
import { QueryParam } from 'utils/constants/queryParam';
import { isoTimeStampLocal } from 'utils/dateUtils';
import { parseEquipmentModelType } from 'utils/equipmentUtils';
import { useFeatureFlags } from 'utils/features';
import { downloadText } from 'utils/fileUtils';
import { t } from 'utils/localize';
import LocalStore from 'utils/localStorageUtils';
import { typedObjectEntries } from 'utils/objectUtils';
import { isAdmin } from 'utils/positionUtils';
import { getApolloSortDirection } from 'utils/tableUtils';
import { defaultVisibleColumns } from './equipment-table-columns';
import useEquipmentManagementData from './hooks/useEquipmentManagementData';
import './ManageEquipment.scss';
import ManageEquipmentActionHeader, {
  ManageEquipmentViewType,
} from './ManageEquipmentActionHeader/ManageEquipmentActionHeader';
import ManageEquipmentDetailsDialog from './ManageEquipmentDetailsDialog/ManageEquipmentDetailsDialog';
import ManageEquipmentFilter from './ManageEquipmentFilter/ManageEquipmentFilter';
import ManageEquipmentMap from './ManageEquipmentMap/ManageEquipmentMap';
import { ManagementEquipment } from './types/types';
import {
  extractCustomFields,
  ICustomFieldTableRow,
  mapCustomFieldsToColumns,
} from 'utils/organizationCustomFieldsUtils';

export interface IManageEquipmentProps {
  className?: ClassName;
}

export interface IManageEquipmentRowInfo {
  updatedByMember: ManagementEquipment['updatedByMember'];
  id: string;
  name: string;
  make: string;
  model: string;
  year: string;
  category: string;
  hourMeter: string;
  costHistory: string;
  type: string;
  equipment: Equipment;
  cursor: string;
  createdOn: string;
  updatedOn: Nullable<string>;
  customField1: Nullable<string>;
  customField2: Nullable<string>;
  customField3: Nullable<string>;
  customField4: Nullable<string>;
  customField5: Nullable<string>;
  certificationLinks: string;
}

const ManageEquipment = ({ className }: IManageEquipmentProps) => {
  const memberSettings = useMemberSettings();
  const updateEquipmentManagementSettings = useEquipmentManagementSettingsUpdate();
  const updateManagementLists = useManagementSettingsUpdate();

  const activeMember = useActiveMember();
  const org = useOrganization();
  const isFeatureEnabled = useFeatureFlags('MANAGEMENT_LISTS', 'MANAGEMENT_LISTS_EQUIPMENT');
  const isCertificationsEnabled = useFeatureFlags('CERTIFICATIONS');
  const { getParam } = useQueryParams();
  const viewType: ManageEquipmentViewType = getParam('viewType') ?? 'list';
  const filterEquipmentId = getParam(QueryParam.EQUIPMENT_ID);
  const filterCategoryId = getParam(QueryParam.EQUIPMENT_CATEGORY_ID);
  const filterEquipmentType = parseEquipmentModelType(getParam(QueryParam.EQUIPMENT_TYPE));
  const filterSearch = getParam(QueryParam.SEARCH);
  const filterPanelDetails = useOpenable({ isOpen: true });
  const deleteDetails = useOpenable();
  const equipmentDetails = useOpenable();
  const createEquipmentDetails = useOpenable();
  const rowSettingsDialog = useOpenable();
  const manageCategoriesDialog = useOpenable();
  const manageMakesDialog = useOpenable();
  const customFieldsDialog = useOpenable();
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const [customFields, setCustomFields] = useState<ICustomFieldTableRow[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<IVisibleTableColumn[]>(getInitialVisibleColumns());
  const [sortDir, setSortDir] = useState<SortDirection>(SortDirection.ASCENDING);
  const [sortBy, setSortBy] = useState<keyof IManageEquipmentRowInfo>('name');
  const [sortIsDirty, setSortIsDirty] = useState<boolean>(false);
  const defaultSectionSize = 15;
  const [checkedRows, setCheckedRows] = useState<IManageEquipmentRowInfo[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const equipmentActions = useEquipment();
  const importDetails = useOpenable();
  const reimportDialogDetails = useOpenable();
  const isManagementListsSearchEnabled = useFeatureFlags('MANAGEMENT_LISTS_SEARCH');
  const [reloadCategories, setReloadCategories] = useState(false);
  const analytics = useManagementAnalytics();
  const { getOrganizationCustomFields } = useOrganizationCustomFields();

  const [rowHeight, setLocalRowHeight] = useState(
    memberSettings?.web?.features?.managementLists?.rowHeight ?? 'standard'
  );
  const setRowHeight = (newSetting: TableCellHeight) => {
    updateManagementLists([{ key: 'rowHeight', payload: newSetting }]);
    setLocalRowHeight(newSetting);
  };
  const useSmallIcon = rowHeight === 'cozy' || rowHeight === 'compact';
  const brand = useBrandTitle();

  const scroller = React.useRef<HTMLElement | null>(null);

  const variables = React.useMemo((): QueryManageEquipmentQueryVariables => {
    const sortDirection = getApolloSortDirection(sortDir);
    let sort;
    const secondarySort =
      sortBy === 'name' ? { lastHours: { runningHours: sortDirection } } : { equipmentName: sortDirection };

    switch (sortBy) {
      case 'name':
        sort = [{ equipmentName: sortDirection }, secondarySort];
        break;
      case 'make':
        sort = [
          { model: { make: { unknown: sortDirection } } },
          { model: { make: { title: sortDirection } } },
          secondarySort,
        ];
        break;
      case 'model':
        sort = [{ model: { unknown: sortDirection } }, { model: { title: sortDirection } }, secondarySort];
        break;
      case 'year':
        sort = [{ model: { year: sortDirection } }, secondarySort];
        break;
      case 'category':
        sort = [{ model: { category: { title: sortDirection } } }, secondarySort];
        break;
      case 'hourMeter':
        sort = [{ lastHours: { runningHours: sortDirection } }, secondarySort];
        break;
      case 'costHistory':
        sort = [secondarySort];
        break;
      case 'type':
        sort = [secondarySort];
        break;
      default:
        sort = [{ [sortBy]: sortDirection }, secondarySort];
        break;
    }

    return {
      filter: {
        id: getGraphQlEqualComparison(filterEquipmentId),
        model:
          filterCategoryId || filterEquipmentType
            ? {
                category: {
                  id: filterCategoryId ? getGraphQlEqualComparison(filterCategoryId) : undefined,
                },
                type: filterEquipmentType ? { equal: filterEquipmentType } : undefined,
              }
            : undefined,
        search: isManagementListsSearchEnabled ? getEquipmentSearch(filterSearch) : undefined,
        deletedOn: { isNull: true },
      },
      sort,
    };
  }, [
    sortDir,
    sortBy,
    filterEquipmentId,
    filterCategoryId,
    filterEquipmentType,
    isManagementListsSearchEnabled,
    filterSearch,
  ]);

  const { data, isError, isLoading, loadedAll, loadAll, refetch, clearData } = useEquipmentManagementData(
    scroller.current,
    variables,
    defaultSectionSize
  );

  useOnMount(() => {
    const migrateData = () => {
      const hasNotMigrated = isNil(memberSettings?.web?.features?.managementLists?.equipment?.localStoreMigrated);
      const localRowHeight: ITableRowHeightSettings = LocalStore.get('table-row-height-settings');
      const managementListRowHeight = localRowHeight?.managementLists;

      if (managementListRowHeight || hasNotMigrated) {
        const localColumnSettings = LocalStore.get('equipment_management_columns');
        updateManagementLists(
          compact([
            managementListRowHeight
              ? {
                  key: 'rowHeight',
                  payload: managementListRowHeight ?? memberSettings?.web?.features?.managementLists?.rowHeight,
                }
              : null,
            hasNotMigrated
              ? {
                  key: 'equipment',
                  payload: {
                    localStoreMigrated: true,
                    columnSettings: localColumnSettings,
                  },
                }
              : null,
          ])
        );
        if (localRowHeight) {
          setLocalRowHeight(managementListRowHeight);
          const isEveryKeyNull = typedObjectEntries(localRowHeight).every(
            ([key, val]) => key === 'managementLists' || isNil(val)
          );
          if (isEveryKeyNull) {
            LocalStore.remove('table-row-height-settings');
          } else {
            LocalStore.set('table-row-height-settings', { ...localRowHeight, managementLists: null });
          }
        }
      }
    };

    migrateData();
  });

  useOnMount(() => {
    getCustomFields();
  });

  const getCustomFields = async () => {
    const organizationCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);
    if (organizationCustomFields) {
      const extractedFields = extractCustomFields(organizationCustomFields);
      setCustomFields(extractedFields);
    }
  };

  useEffect(() => {
    setVisibleColumns(getInitialVisibleColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields]);

  function getInitialVisibleColumns() {
    const defaultColumnsWithCustomFields = [...defaultVisibleColumns, ...mapCustomFieldsToColumns(customFields)];

    if (isCertificationsEnabled) {
      defaultColumnsWithCustomFields.push({
        key: 'certificationLinks',
        sort: false,
        title: t('Req. Certifications'),
        visible: true,
        visibleByDefault: true,
      });
    }

    const savedColumns = memberSettings?.web?.features?.managementLists?.equipment?.columnSettings;
    const merged = _.sortBy(
      mergeListsWithOverride(defaultColumnsWithCustomFields, savedColumns ?? [], 'key', [
        'position',
        'visible',
      ]) as IVisibleTableColumn[],
      (item) => item.position ?? 0
    );
    return merged;
  }

  function handleVisibleColumnsChange(newVisibleColumns: IVisibleTableColumn[]) {
    updateEquipmentManagementSettings([{ key: 'columnSettings', payload: newVisibleColumns }]);
    setVisibleColumns(newVisibleColumns);
  }

  const sortedRows = React.useMemo(() => {
    // Porting over similar behavior but this won't work with lazy loading correctly
    function sortRows(rows: IManageEquipmentRowInfo[]): IManageEquipmentRowInfo[] {
      switch (sortBy) {
        case 'hourMeter': {
          // Always show empty hour meter values at end of list regardless of sort. Only applicable when sorting hourMeter column.
          const [hasHourMeterRows, noHourMeterRows] = partition(rows, (row) => row.hourMeter !== '---');
          return hasHourMeterRows.concat(noHourMeterRows);
        }
        case 'costHistory': {
          // Always show empty cost history values at end of list regardless of sort. Only applicable when sorting costHistory column.
          const [hasCostHistoryRows, noCostHistoryRows] = partition(rows, (row) => row.costHistory !== '---');
          return hasCostHistoryRows.concat(noCostHistoryRows);
        }
        case 'type': {
          const sortedRows = _.sortBy(rows, (r) => r.type.toLowerCase(), sortDir.toLowerCase());
          return sortDir.toLowerCase() === 'asc' ? sortedRows : sortedRows.reverse();
        }
        default:
          return rows;
      }
    }

    return sortRows(data ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function handleSort(sort: ISortPayload<IManageEquipmentRowInfo>) {
    setCheckedRows([]);
    setSortBy(sort.sortBy as any);
    setSortDir(sort.sortDir);
    setSortIsDirty(true);
    refetch();
  }

  function showManageEquipment() {
    return (activeMember.position?.manageEquipment ?? false) && org.trackEquipment ? org.trackEquipment : false;
  }

  function renderEmptyState() {
    return (
      <EmptyState
        title={t('No Equipment')}
        subtitle={t('Click on the create button above to add a piece of equipment.')}
      />
    );
  }

  function filterColumns(cols: Array<ITableColumn<IManageEquipmentRowInfo>>) {
    const columnMap = _.keyBy(visibleColumns, 'key');
    const visibleCols = _.sortBy(
      cols.filter((column: ITableColumn<IManageEquipmentRowInfo>) => {
        const match = columnMap[column.key];
        return match && match.visible === true;
      }),
      (column, index) => columnMap[column.key].position ?? index
    );
    visibleCols.unshift(getActionColumn());

    return visibleCols;
  }

  function handleRowClick(row: IManageEquipmentRowInfo) {
    setSelectedEquipment(row.equipment);
    equipmentDetails.open();
  }

  async function handleCheckAll(checked: boolean) {
    if (checked && !loadedAll) {
      const result = await loadAll();
      setCheckedRows(result);
    }
  }

  function handleSelectChange(newCheckedRows: IManageEquipmentRowInfo[]) {
    setCheckedRows(newCheckedRows);
  }

  function handleClearCheckedRows() {
    setCheckedRows([]);
  }

  async function handlePrint() {
    analytics.recordClickPrintEquipment();
    await loadAll();
    setImmediate(() => window.print());
  }

  function handleCsvExport(reimport: boolean) {
    return async () => {
      if (checkedRows.length > 0) {
        await exportToCsv(checkedRows, false);
      } else {
        const allRows = await loadAll();
        await exportToCsv(allRows, reimport);
      }
      analytics.recordClickExportEquipment();
    };
  }

  async function exportToCsv(allRows: IManageEquipmentRowInfo[], reimport: boolean) {
    if (allRows.length !== 0) {
      const orgCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);

      const csv = Papa.unparse(
        allRows
          .map((r) => r.equipment)
          .map((e) =>
            convertManageEquipmentToCsvEquipment(e, reimport, isAdmin(activeMember.position), orgCustomFields)
          )
      );
      handleClearCheckedRows();
      const title = reimport ? t('equipment-reimport') : t('equipment-export');
      downloadText(csv, `${brand}-${title}-${isoTimeStampLocal()}.csv`);
    } else {
      setErrorToastMessage(t('There is no data to export.'));
    }
  }

  function handleErrorToastClose() {
    setErrorToastMessage('');
  }

  function handleDelete() {
    if (selectedEquipment) {
      deleteEquipment([selectedEquipment]);
      setSelectedEquipment(null);
      if (equipmentDetails.isOpen) {
        equipmentDetails.close();
      }
    }
  }

  async function deleteEquipment(equipment: Equipment[] | IEquipment[]) {
    const promises = equipment.map(({ id }) => {
      return equipmentActions.deleteEquipment(id);
    });

    await Promise.all(promises);
    refetch();
  }

  function handleCreateSubmit() {
    createEquipmentDetails.close();
    refetch();
  }

  function handleEditClose() {
    equipmentDetails.close();
    setSelectedEquipment(null);
  }

  const handleEquipmentCategoriesClose = () => {
    manageCategoriesDialog.close();
    setReloadCategories((prev) => !prev);
  };

  const handleEquipmentMakesClose = () => {
    manageMakesDialog.close();
  };

  function getActionColumn() {
    return {
      align: TextAlign.CENTER,
      cell: (row: IManageEquipmentRowInfo, _col: ITableColumn<IManageEquipmentRowInfo>) => (
        <MoreButton
          className={`${useSmallIcon ? 'small-icon' : ''}`}
          position={Position.BOTTOM_START}
          renderContent={(close: () => void) => {
            return (
              <ButtonList>
                <Button
                  onClick={async (e: React.MouseEvent<Element, MouseEvent>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    close();
                    setSelectedEquipment(row.equipment);
                    deleteDetails.open();
                  }}
                >
                  {t('Delete')}
                </Button>
              </ButtonList>
            );
          }}
        />
      ),
      cellClassName: 'px-1 no-print',
      headerClassName: 'no-print',
      key: 'actions',
      size: '60px',
      title: '',
      visibleByDefault: true,
    };
  }

  function renderList() {
    return (
      <PanelContent forwardRef={(ref) => (scroller.current = ref)} flex={true}>
        <FlexItem flexGrow={1}>
          <TemplatedTable
            emptyTemplate={renderEmptyState()}
            cols={filterColumns(visibleColumns)}
            data={sortedRows ?? []}
            minWidth={filterColumns(visibleColumns).length * 220 + 'px'}
            lazyLoad={false}
            sortBy={sortBy}
            sortDir={sortDir}
            sortIsDirty={sortIsDirty}
            selected={checkedRows}
            onSelectChange={handleSelectChange}
            onCheckAllChange={handleCheckAll}
            onSort={handleSort}
            onRowClick={handleRowClick}
            strokeCols={false}
            cellHeight={rowHeight}
            isError={isError}
            isLoading={isLoading}
          />
        </FlexItem>
      </PanelContent>
    );
  }

  function renderMap() {
    return (
      <ManageEquipmentMap
        onDetailsClick={(e) => {
          setSelectedEquipment(e);
          equipmentDetails.open();
        }}
        onDirectionsClick={(e) => {
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${e.lastLocation?.latitude},${e.lastLocation?.longitude}`,
            '_blank'
          );
        }}
        equipmentId={filterEquipmentId}
        categoryId={filterCategoryId}
        updatedEquipmentId={equipmentDetails.isOpen ? selectedEquipment?.id : undefined}
        search={filterSearch}
      />
    );
  }

  function renderMain() {
    return (
      <>
        {!showManageEquipment() && <Navigate to="/not-found" />}
        <Panel className={classes}>
          <LegacyMainHeader
            title={t('Equipment')}
            className="no-print"
            rightContent={
              <Button type="icon" onClick={filterPanelDetails.toggle}>
                <Icon svg={ListIcon} />
              </Button>
            }
          />
          <PanelContent>
            <Panel>
              <ManageEquipmentActionHeader
                checkedEquipment={checkedRows.map((r) => r.equipment)}
                clearCheckedEquipment={handleClearCheckedRows}
                openCreateDialog={createEquipmentDetails.open}
                onExportEquipment={handleCsvExport(false)}
                onImportEquipment={importDetails.open}
                onPrint={handlePrint}
                visibleColumns={visibleColumns}
                handleVisibleColumnsChange={handleVisibleColumnsChange}
                onDelete={deleteEquipment}
                className="no-print"
                onEditUsingSpreadsheetClick={reimportDialogDetails.open}
                viewType={viewType}
                onRowHeightClick={rowSettingsDialog.open}
                onManageCategories={manageCategoriesDialog.open}
                onManageMakes={manageMakesDialog.open}
                onManageCustomFields={customFieldsDialog.open}
              />
              <h2 className="print mb-5">{t('Equipment')}</h2>
              <PanelContent>{viewType === 'list' ? renderList() : renderMap()}</PanelContent>
            </Panel>
            <Panel medium={true} strokeLeft={true} className="no-print" open={filterPanelDetails.isOpen}>
              <ManageEquipmentFilter reloadCategories={reloadCategories} />
            </Panel>
          </PanelContent>
          <Toast isOpen={errorToastMessage.length !== 0} onClose={handleErrorToastClose} theme={Theme.DANGER}>
            {errorToastMessage}
          </Toast>
        </Panel>
      </>
    );
  }

  const classes = classNames('manage-equipment', className);

  return (
    <>
      <Helmet>
        <title>{t('Manage Equipment')}</title>
      </Helmet>
      {isFeatureEnabled ? renderMain() : FeatureUnavailable()}
      {selectedEquipment && (
        <DeleteConfirmationDialog
          isOpen={deleteDetails.isOpen}
          onClose={deleteDetails.close}
          onDelete={handleDelete}
          title={t('Delete equipment?')}
          showHeaderDivider={false}
        >
          {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
        </DeleteConfirmationDialog>
      )}
      {selectedEquipment && (
        <ManageEquipmentDetailsDialog
          isOpen={equipmentDetails.isOpen}
          onClose={handleEditClose}
          equipment={selectedEquipment}
          onEdit={() => refetch()}
          onDelete={handleDelete}
        />
      )}
      <EquipmentCreateFormDialog
        isOpen={createEquipmentDetails.isOpen}
        onClose={createEquipmentDetails.close}
        onSubmit={handleCreateSubmit}
      />
      <CSVImportEquipmentContainer
        importDialogOpen={importDetails.isOpen}
        onImportDialogClose={importDetails.close}
        onImportComplete={clearData}
        onReimportDialogClose={reimportDialogDetails.close}
        reimportDialogOpen={reimportDialogDetails.isOpen}
        onDownloadList={handleCsvExport(true)}
      />
      <RowSettingsDialog
        isOpen={rowSettingsDialog.isOpen}
        onClose={rowSettingsDialog.close}
        initialValue={rowHeight}
        onSubmit={setRowHeight}
      />
      <ManageEquipmentCategoriesDialog
        isOpen={manageCategoriesDialog.isOpen}
        onClose={handleEquipmentCategoriesClose}
      />
      <ManageEquipmentMakesDialog isOpen={manageMakesDialog.isOpen} onClose={handleEquipmentMakesClose} />
      <CustomFieldsDialog
        isOpen={customFieldsDialog.isOpen}
        onClose={customFieldsDialog.close}
        type={OrganizationCustomFieldsTypes.Equipment}
      />
    </>
  );
};

export const getEquipmentSearch = (search: string | null): EquipmentSearch | undefined => {
  if (search && search.length) {
    return {
      type: SearchType.Contains,
      fields: [EquipmentSearchFields.EquipmentName, EquipmentSearchFields.MakeName, EquipmentSearchFields.ModelName],
      value: search,
    };
  } else {
    return undefined;
  }
};

export default ManageEquipment;
